import { useNavigate } from "react-router-dom";
import { useRolePermission } from "../../hooks/useRolePermission";
import { useState, useEffect, useMemo } from "react";
import FetchData, { DeleteEntry, PaginationFetchData } from "../../providers/dataProvider";
import FactoryOrderGrid from "../../components/factoryOrder/FactoryOrderGrid";
import { CheckNull } from "../../utils/Validate";
import { SearchComponent } from "../../components/common/SearchComponent";
import DeleteModal from "../../components/common/DeleteModal";
import ErrorMessage from "../../components/common/ErrorMessage";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import { ErrorMessages, SuccessMessages } from "../../constants/MessageConstants";
import AdvancedFilterDropdown from "../../components/common/AdvancedFilterDropdown";

const FactoryOrderList = () => {
     const navigate = useNavigate();
      const { menuRights } = useRolePermission("Factory Order");
      const [search, setSearch] = useState("");
      const [pageSize, setPageSize] = useState(25);
      const [pageNumber, setPageNumber] = useState(1);
      const [customerId, setCustomerId] = useState(null);
      const [projectId, setProjectId] = useState(null);
      const [status, setStatus] = useState("");
      const [releasedDateRange, setReleasedDateRange] = useState([null, null]);
      const [createdDateRange, setCreatedDateRange] = useState([null, null]);
      const [deleteShow, setDeleteShow] = useState({ show: false, item: "", title: '', textCode: '', text: '', loading: false });
      const [createMsg, setCreateMsg] = useState(null)
      const [successShow, setSuccessShow] = useState(false)
      const [errorShow, setErrorShow] = useState({
        show: false,
        errorMessage: ""
      });
      const [displayOrderList, setDisplayOrderList] = useState(null);
    
      const initialFilterState = {
        PageNumber: 1,
        PageSize: 25,
        CustomerId: "",
        ProjectId: "",
        Status: "",
        ReleasedFromDate: "",
        ReleasedToDate: "",
        CreatedFromDate: "",
        CreatedToDate: "",
        SearchTerm: "",
        userId: "",
        userType: ""
      };
    
      const [filterData, setFilterData] = useState(initialFilterState);
    
      const { data: countryData } = FetchData("country");
      const { data: distributorData } = FetchData("distributor");
      const { data: projectData } = FetchData("project");
      const { data: customerData } = FetchData("Customer");
      const { data: currencyData } = FetchData("currency");
      const { data: orderDataList } = FetchData("order", {
    pageNumber: 1,
    pageSize: 100,
});
      const { data: assignedEngineerList } = FetchData("userprofile");
      const { data: orderList, loading } = PaginationFetchData("factoryOrder", filterData);
      // Load the complete result set for the client-side search fallback. The
      // normal request above remains paged for the standard listing view.
      const searchFilterData = useMemo(() => ({
        ...filterData,
        PageNumber: 1,
        PageSize: 100,
      }), [filterData]);
      const { data: searchableOrderList } = PaginationFetchData("factoryOrder", searchFilterData);

      const projectOptions = useMemo(
        () =>
          (Array.isArray(projectData?.payload)
            ? projectData.payload
            : Array.isArray(projectData)
              ? projectData
              : [])
            .map((item) => ({
              label: item?.projectName,
              value: item?.id,
            }))
            .filter((item) => item.label),
        [projectData]
      );

      const customerOptions = useMemo(
        () =>
          (Array.isArray(customerData?.payload)
            ? customerData.payload
            : Array.isArray(customerData)
              ? customerData
              : [])
            .map((item) => ({
              label: item?.customerName,
              value: item?.id,
            }))
            .filter((item) => item.label),
        [customerData]
      );

      const statusOptions = useMemo(() => [
        { label: "Registered", value: "0" },
        { label: "Released", value: "1" },
        { label: "Cancelled", value: "2" },
        { label: "Pending approval", value: "3" },
        { label: "Approval rejected", value: "4" },
      ], []);

      useEffect(() => {
        setDisplayOrderList(orderList);
      }, [orderList]);

      // The factory-order endpoint currently returns the paged result without
      // applying SearchTerm, so filter the returned rows as a UI fallback.
      const searchedOrderList = useMemo(() => {
        const query = filterData?.SearchTerm?.trim().toLowerCase();
        const source = query
          ? (searchableOrderList || displayOrderList || orderList)
          : (displayOrderList || orderList);
        if (!query || !source?.payload) return source;

        const payload = source.payload.filter((item) =>
          [
            item?.factoryReleaseReference,
            item?.customerOrderReference,
            item?.orderReference,
            item?.projectName,
            item?.customerName,
            item?.countryName,
            item?.status,
          ]
            .filter((value) => value !== null && value !== undefined)
            .some((value) => String(value).toLowerCase().includes(query))
        );

        return { ...source, payload, totalRecords: payload.length };
      }, [displayOrderList, orderList, searchableOrderList, filterData?.SearchTerm]);

      const formatFilterDate = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
    
      useEffect(() => {
        let pageNo = pageNumber
        if (orderList?.totalRecords <= pageSize) pageNo = 1;
        else if (filterData?.PageNumber > Math.ceil(orderList?.totalRecords / pageSize)) pageNo = 1
        setFilterData({
          ...filterData,
          PageNumber: pageNo,
          PageSize: pageSize,
        });
      }, [pageNumber, pageSize]);
    
      const handleEdit = (item) => {
        if (item)
          navigate("/add-new-factory-order", {
            state: {
              row: item,
              id: item?.id,
              canAdd: menuRights?.canAdd,
              canEdit: menuRights?.canEdit,
            },
          });
      };
    
      const handleView = (item, projectDetails, distributorDetails, countryDetails, customerDetails, currencyDetails, assignedEngineerDetails) => {
        navigate('/view-factory-order', {
          state: {
            item: item,
            canView: menuRights?.canView,
            customerOrderId: item?.customerOrderId,
            projectDetails: projectDetails,
            distributorDetails: distributorDetails,
            countryDetails: countryDetails,
            customerDetails: customerDetails,
            currencyDetails: currencyDetails,
            assignedEngineerDetails: assignedEngineerDetails
          }
        });
      };

      const handleAdd = () => {
        navigate("/add-new-factory-order", {
          state: {
            canAdd: menuRights?.canAdd,
            canEdit: menuRights?.canEdit,
          },
        });
      };
    
      const ClearFilter = () => {
        setCustomerId(null);
        setProjectId(null);
        setStatus(null);
        setReleasedDateRange([null, null]);
        setCreatedDateRange([null, null]);
        setSearch("");
        setFilterData((prev) => ({
          ...prev,
          CustomerId: "",
          ProjectId: "",
          Status: "",
          ReleasedFromDate: "",
          ReleasedToDate: "",
          CreatedFromDate: "",
          CreatedToDate: "",
          SearchTerm: "",
          PageNumber: 1,
        }));
        setPageNumber(1)
      };

      const ApplyFilter = (values = {}) => {
        setCustomerId(values.customerId ?? null);
        setProjectId(values.projectId ?? null);
        setStatus(values.status ?? null);
        setReleasedDateRange(values.releasedDateRange ?? [null, null]);
        setCreatedDateRange(values.createdDateRange ?? [null, null]);

        setPageNumber(1);
        setFilterData((prev) => ({
          ...prev,
          CustomerId: values.customerId?.value || "",
          ProjectId: values.projectId?.value || "",
          Status: values.status?.value ?? "",
          ReleasedFromDate: formatFilterDate(values.releasedDateRange?.[0]),
          ReleasedToDate: formatFilterDate(values.releasedDateRange?.[1]),
          CreatedFromDate: formatFilterDate(values.createdDateRange?.[0]),
          CreatedToDate: formatFilterDate(values.createdDateRange?.[1]),
          SearchTerm: "",
          PageSize: pageSize,
          PageNumber: 1,
        }));
        setSearch("");
      };

      const ApplySearch = () => {
        setPageNumber(1);
        setFilterData((prev) => ({
          ...prev,
          SearchTerm: search.trim(),
          PageNumber: 1,
        }));
      };

      const removeSearch = () => {
        setSearch("");
        setPageNumber(1);

        setFilterData(prevData => ({
          ...prevData,
          SearchTerm: "",
          PageNumber: 1
        }));
      };
    
      const RefreshList = () => {
        setSearch("");
        setPageNumber(1);
        setPageSize(25);
        setCustomerId(null);
        setProjectId(null);
        setStatus(null);
        setReleasedDateRange([null, null]);
        setCreatedDateRange([null, null]);
        setFilterData(initialFilterState);
      };
    
      const handleDelete = (item) => {
        setDeleteShow({
          show: true,
          item: item,
          title: 'You want delete this Order?',
          textCode: 'Order Reference',
          text: item?.factoryReleaseReference,
          loading: false
        })
      }
    
      const HandleCancelDelete = () => {
        setDeleteShow({ show: false, item: "", title: '', textCode: '', text: '', loading: false })
      }
      const HandleConfirmDelete = (item) => {
        setDeleteShow({
          ...deleteShow,
          loading: true
        })
        DeleteEntry("factoryOrder", item, setCreateMsg, SuccessMessages.DELETED("Factory Order"), ErrorMessages.DELETED_ERROR("Factory Order"))
      }
    
      const SuccessDoneClick = () => {
        setSuccessShow(false);
    
        setPageNumber(1);
        setFilterData((prev) => ({
          ...prev,
          PageNumber: 1,
        }));
      };
    
      const ErrorDoneClick = () => {
        setErrorShow({
          show: false,
          errorMessage: ""
        })
        setCreateMsg(null);
      }
      useEffect(() => {
        if (createMsg?.successData === "Successful") {
          if (CheckNull(createMsg?.message) !== "") {
            const deletedOrderId = deleteShow?.item?.id;
            setDeleteShow({ show: false, item: "", title: '', textCode: '', text: '', loading: false })
            setSuccessShow(true)
            if (deletedOrderId != null) {
              setDisplayOrderList((prev) => {
                if (!prev?.payload?.length) return prev;
    
                const nextPayload = prev.payload.filter((item) => item.id !== deletedOrderId);
                return {
                  ...prev,
                  payload: nextPayload,
                  totalRecords: Math.max(0, (prev.totalRecords || 0) - 1),
                };
              });
            }
    
          }
        } else {
    
          if (CheckNull(createMsg?.message) !== "") {
            setDeleteShow({ show: false, item: "", title: '', textCode: '', text: '', loading: false })
            setErrorShow({ show: true, errorMessage: createMsg.message })
            setSuccessShow(false);
          }
        }
      }, [createMsg]);
    
      const handleKeyPress = (e) => {
        if (e.key === "Enter") {
          ApplySearch();
        }
      };
    
      const addNew = () => { navigate('/add-new-factory-order', { state: { canAdd: menuRights?.canAdd, canEdit: menuRights?.canEdit } }) }
      const hasActiveFilters = Boolean(customerId || projectId || status || releasedDateRange?.[0] || releasedDateRange?.[1] || createdDateRange?.[0] || createdDateRange?.[1]);
    
      return (
    <>
      <section id="factoryOrderList">
        <div className="container-fluid">
          <div className="row">
            <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 px-0 py-1">
                <div className="card">
                <div className="card-body">
                  <div className="row search-area-row">
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-4 col-sm-12 col-xs-12 my-auto p-0">
                      <div>
                        <p>{searchedOrderList?.totalRecords ?? orderList?.totalRecords ?? orderList?.payload?.length ?? 0} Results Found</p>
                      </div>
                    </div>
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-8 col-sm-12 col-xs-12 my-auto p-0">
                      <SearchComponent
                        filteredResults={searchedOrderList?.payload || orderList?.payload || []}
                        search={search}
                        handleKeyDown={handleKeyPress}
                        setSearch={setSearch}
                        removeSearch={removeSearch}
                        ApplySearch={ApplySearch}
                        RefreshList={RefreshList}
                        data={orderList?.payload || []}
                        addNew={handleAdd}
                        menuRights={menuRights}
                        placeholder="Search by Factory Order Reference"
                        hasActiveFilters={hasActiveFilters}
                         filterContent={(closeFilter) => (
                        <AdvancedFilterDropdown
                          ApplyFilter={ApplyFilter}
                          ClearFilter={ClearFilter}
                          startWithFirstOnly
                          onCloseFilter={closeFilter}
                          fields={[
                            {
                              value: "customerId",
                              label: "Customer",
                              placeholder: "Select customer",
                              type: "select",
                              options: customerOptions,
                              valueObject: customerId,
                              setState: setCustomerId,
                              onChange: (e) => setCustomerId(e),
                            },
                            {
                              value: "projectId",
                              label: "Project",
                              placeholder: "Select project",
                              type: "select",
                              options: projectOptions,
                              valueObject: projectId,
                              setState: setProjectId,
                              onChange: (e) => setProjectId(e),
                            },
                            {
                              value: "status",
                              label: "Status",
                              placeholder: "Select status",
                              type: "select",
                              options: statusOptions,
                              valueObject: status,
                              onChange: (e) => setStatus(e),
                            },
                            {
                              value: "releasedDateRange",
                              label: "Released Date",
                              type: "dateRange",
                              valueObject: releasedDateRange,
                              onChange: (value) => setReleasedDateRange(value),
                            },
                            {
                              value: "createdDateRange",
                              label: "Created Date",
                              type: "dateRange",
                              valueObject: createdDateRange,
                              onChange: (value) => setCreatedDateRange(value),
                            },
                          ]}
                        />
                      )}
          />
                    </div>
                  </div>
                  {/* <!-- search-area-sec ends --> */}
                </div>
              </div>
            </div>
          </div>

          <FactoryOrderGrid
             loading={loading}
            orderList={searchedOrderList || orderList}
            filterData={filterData}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            pageSize={pageSize}
            distributorData={distributorData}
            projectData={projectData}
            countryData={countryData}
            customerData={customerData}
            currencyData={currencyData}
            assignedEngineerList={assignedEngineerList}
            handleEdit={handleEdit}
            handleView={handleView}
            handleDelete={handleDelete}
            menuRights={menuRights}
            orderDataList={orderDataList}
          />
        </div>
         <DeleteModal deleteShow={deleteShow} handleCancelDelete={HandleCancelDelete} handleConfirmDelete={HandleConfirmDelete} />
          <ErrorMessage Show={errorShow?.show} errorMessage={errorShow?.errorMessage} ErrorDoneClick={ErrorDoneClick} />
          <SaveSuccessModel show={successShow} createMsg={createMsg} setShow={setSuccessShow} SuccessDoneClick={SuccessDoneClick} />
      </section>
    </>
  );
}

export default FactoryOrderList
